import { supabase } from "@/integrations/supabase/client";

// Função utilitária para verificar se o paciente já tem registros anteriores
async function checkIfPatientHasExistingRecords(patientId: string, currentSessionId?: string): Promise<boolean> {
  try {
    // Verificar se já existem sessões smartfile com structured_records (excluindo a sessão atual)
    let query = supabase
      .from("smartfile_sessions")
      .select("id, structured_records")
      .eq("patient_id", patientId)
      .not("structured_records", "is", null);
    
    // Excluir a sessão atual se fornecida
    if (currentSessionId) {
      query = query.neq("id", currentSessionId);
    }
    
    const { data: existingSessions, error: sessionsError } = await query;

    if (sessionsError) {
      console.error("Error checking existing sessions:", sessionsError);
    } else if (existingSessions && existingSessions.length > 0) {
      // Verificar se alguma sessão tem registros estruturados não vazios
      const hasRecords = existingSessions.some(session => {
        if (!session.structured_records) return false;
        try {
          const records = Array.isArray(session.structured_records) 
            ? session.structured_records 
            : typeof session.structured_records === 'string'
            ? JSON.parse(session.structured_records)
            : null;
          return records && Array.isArray(records) && records.length > 0;
        } catch {
          return false;
        }
      });
      if (hasRecords) return true;
    }

    // Verificar se já existem documentos processados
    const { count: documentsCount, error: docsError } = await supabase
      .from("documents_onboarding")
      .select("*", { count: "exact", head: true })
      .eq("patient_id", patientId)
      .in("status", ["processed", "completed"]);

    if (docsError) {
      console.error("Error checking existing documents:", docsError);
    } else if (documentsCount && documentsCount > 0) {
      return true;
    }

    // Verificar se já existem lab_results de OUTRAS sessões/documentos
    // (excluir a sessão atual - o trigger SQL já inseriu os da sessão atual antes de saveStructuredRecords)
    let labQuery = supabase
      .from("lab_results_onboarding")
      .select("*", { count: "exact", head: true })
      .eq("patient_id", patientId);
    if (currentSessionId) {
      labQuery = labQuery.or(`source_session_id.is.null,source_session_id.neq.${currentSessionId}`);
    }
    const { count: labResultsCount, error: labError } = await labQuery;

    if (labError) {
      console.error("Error checking existing lab results:", labError);
    } else if (labResultsCount && labResultsCount > 0) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error in checkIfPatientHasExistingRecords:", error);
    // Em caso de erro, assumir que não é o primeiro documento para não quebrar o fluxo
    return true;
  }
}

// Função utilitária para marcar visão 360 e análise global como desatualizados
async function markBothStale(patientId: string): Promise<void> {
  const { error } = await supabase
    .from("patient_data_stale_flags" as any)
    .upsert({
      patient_id: patientId,
      vision360_stale_at: new Date().toISOString(),
      global_analysis_stale_at: new Date().toISOString(),
    } as any, {
      onConflict: "patient_id",
    });

  if (error) {
    console.error("Error marking both stale:", error);
    // Não lançar erro para não quebrar o fluxo principal
  }
}

export type StructuredRecord = {
  event_date: string;
  record_type: "diagnóstico" | "exame" | "nota" | "anamnese" | "receita" | "treino" | "plano_de_refeicao";
  created_at: string;
  text: string;
  image_url: string | null;
  json_data: any;
  ai_note: string;
  risk_level: "alto" | "moderado" | "baixo";
};

export async function callStructureRecords(
  extracted_text: string,
  professional: { health_area?: string | null },
  file_meta: {
    uploaded_at_iso: string;
    file_type?: string | null;
    file_url?: string | null;
  }
) {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/miafile-structure-records`;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const r = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey,
      authorization: `Bearer ${apikey}`,
    },
    body: JSON.stringify({ extracted_text, professional, file_meta }),
  });

  const t = await r.text();
  let j: any;
  try {
    j = JSON.parse(t);
  } catch {
    j = { error: t };
  }

  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);

  // Transformar estrutura para compatibilidade (edge function retorna 'date' e 'type', precisamos de 'event_date' e 'record_type')
  if (j.records && Array.isArray(j.records)) {
    j.records = j.records.map((record: any) => ({
      ...record,
      event_date: record.date || record.event_date,
      record_type: record.type || record.record_type,
      // Garantir que tudo esteja em json_data também
      json_data: {
        ...record.json_data,
        ...record, // Manter todos os dados originais
      }
    }));
  }

  return j as { records: StructuredRecord[]; errors?: any[] };
}

// Funções auxiliares para normalização
function mapTypeToRecordType(type: string): string {
  const typeMap: Record<string, string> = {
    'meal_plan': 'plano_de_refeicao',
    'lab_result': 'exame',
    'exam': 'exame_imagem',
    'report': 'laudo_medico',
    'clinical_note': 'nota',
    'physiotherapy_evolution': 'evolucao',
    'nutritional_assessment': 'anamnese',
    'training_plan': 'treino',
    'body_composition': 'anamnese',
    'vitals': 'exame',
    'psychotherapy_session': 'anotacao',
    'chiropractic_adjustment': 'anotacao',
    'aesthetic_protocol': 'anotacao',
    'anthropometry': 'anamnese',
    'anamnesis': 'anamnese',
    'result': 'exame',
    'results': 'exame',
  };
  return typeMap[type] || 'exame';
}

function generateDefaultText(itemData: any): string {
  if (itemData.type === 'meal_plan') {
    const mealsCount = Array.isArray(itemData.meals) ? itemData.meals.length : 0;
    return `${itemData.plan_name || 'Plano Alimentar'} com ${mealsCount} refeições`;
  }

  if (itemData.type === 'exam') {
    const examType = itemData.exam_type || 'Exame de Imagem';
    const region = itemData.body_region ? ` - ${itemData.body_region}` : '';
    return `${examType}${region}`;
  }

  if (itemData.type === 'report') {
    const reportType = itemData.report_type || 'Laudo Médico';
    const specialty = itemData.specialty ? ` (${itemData.specialty})` : '';
    return `${reportType}${specialty}`;
  }

  if (itemData.test_name) return itemData.test_name;
  if (itemData.exercise) return `Exercício: ${itemData.exercise}`;
  if (itemData.description) return itemData.description;
  if (itemData.plan_name) return itemData.plan_name;
  return 'Registro de saúde';
}

export async function saveStructuredRecords(
  patient_id: string,
  session_id: string,
  items: StructuredRecord[]
) {
  const user = (await supabase.auth.getUser()).data.user;
  if (!user) throw new Error("User not authenticated");

  console.log(`[saveStructuredRecords] Processando ${items.length} registros para lab_results (patient_id=${patient_id}, session_id=${session_id})`);

  // Função auxiliar para normalizar data para formato ISO
  const normalizeDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;

    // Se já está em formato ISO (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      return dateStr.split('T')[0]; // Remove hora se houver
    }

    // Se está em formato brasileiro (DD/MM/YYYY)
    const brMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (brMatch) {
      const [, day, month, year] = brMatch;
      return `${year}-${month}-${day}`;
    }

    // Tentar parsear com Date e converter para ISO
    try {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toISOString().split('T')[0];
      }
    } catch {
      // Ignorar erros de parsing de data
    }

    return null;
  };

  // ✅ ESTRATÉGIA HÍBRIDA: 
  // - structured_records é a fonte de verdade (já salvo em handleFinalize)
  // - lab_results_onboarding é sincronizado automaticamente via trigger SQL
  // - Esta função não precisa mais inserir diretamente em lab_results_onboarding

  console.log(`[saveStructuredRecords] ✅ Registros já salvos em structured_records. Trigger SQL sincronizará lab_results_onboarding automaticamente.`);

  // Verificar se há lab_results nos registros (apenas para log)
  const labResultsCount = items.filter((item: any) =>
    (item.type === 'lab_result' || item.type === 'results') &&
    item.test_name &&
    (item.value !== null && item.value !== undefined)
  ).length;

  if (labResultsCount > 0) {
    console.log(`[saveStructuredRecords] 📊 ${labResultsCount} lab_result(s) detectado(s) nos registros. Sincronização automática via trigger SQL.`);
  } else {
    console.log(`[saveStructuredRecords] ⚠️ Nenhum lab_result encontrado nos registros`);
  }

  // Marcar visão 360 e análise global como desatualizados quando novos registros são salvos
  // MAS: não marcar se for o primeiro documento do paciente (onboarding)
  if (items.length > 0) {
    // Verificar se já existem registros anteriores para este paciente (excluindo a sessão atual)
    const hasExistingRecords = await checkIfPatientHasExistingRecords(patient_id, session_id);
    
    // Só marcar como stale se já existem registros anteriores
    // Se for o primeiro documento, não precisa marcar como stale
    if (hasExistingRecords) {
      await markBothStale(patient_id);
    } else {
      console.log(`[saveStructuredRecords] ⏭️ Primeiro documento do paciente - não marcando como stale`);
    }
  }
}

export const MAX_RECORDS = 30;

export function overBetaLimit(items: StructuredRecord[], limit = MAX_RECORDS) {
  return (items?.length || 0) > limit;
}
